import axios from 'axios'

import { SESSION_STORAGE_KEYS, safeSessionStorage } from '@/constants/storageKeys'
import { logger } from './logger'
import { useAuthStore } from '@/store/authStore'
import { requestTokenRefreshFromNative } from './nativeAuth'

import { getCheckTokenApi } from '@/api/authApi'

// 토큰 갱신 중인지 확인하는 플래그
let isRefreshing = false
// 토큰 갱신 대기 중인 요청들을 저장하는 배열
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: any) => void
}> = []

// 대기 중인 요청들을 처리하는 함수
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token!)
    }
  })

  failedQueue = []
}

// React Query와 연동을 위한 전역 상태
let queryClient: any = null

export const setQueryClient = (client: any) => {
  queryClient = client
}

// 에러 핸들러 인터페이스 정의
export interface ErrorHandlerOptions {
  showErrorToast: (message: string) => void
  showErrorModal: (message: string, onConfirm?: () => void) => void
  showLoginRedirectModal: (message: string) => void
}

export interface ErrorHandlerResult {
  shouldRetry: boolean
  retryWithNewToken?: string
  handled: boolean
}

/**
 * 로그아웃 처리 함수
 */
const handleLogout = (message: string = '로그인 세션이 만료되었습니다.') => {
  logger.auth('로그아웃 처리:', message)

  // 메모리 인증 정보 삭제
  useAuthStore.getState().clearAuth()

  // React Query 캐시 무효화
  if (queryClient) {
    queryClient.clear()
  }

  // 현재 경로 저장 후 로그인 페이지로 이동
  const currentPath = window.location.pathname + window.location.search
  safeSessionStorage.set(SESSION_STORAGE_KEYS.REDIRECT_PATH, currentPath)
  window.location.href = '/auth/login'
}

/**
 * 통합 에러 처리 함수 (기존 errorHandler.ts 기능 포함)
 * 1. 에러 발생 시 토큰 유효성 확인
 * 2. 토큰이 유효하면 error.data.message 표시
 * 3. 토큰이 유효하지 않으면 재발급 시도
 * 4. 재발급 실패 시 로그인 화면으로 이동
 * 5. 재발급 성공 시 원래 요청 재시도 준비
 */
export const handleError = async (
  error: any,
  errorHandlerOptions: ErrorHandlerOptions
): Promise<ErrorHandlerResult> => {
  const { showErrorToast, showLoginRedirectModal } = errorHandlerOptions

  logger.debug('에러 처리 시작', error)

  // 1. 현재 액세스 토큰 확인 (메모리에서 조회)
  const accessToken = useAuthStore.getState().getAccessToken()
  if (!accessToken) {
    logger.warn('액세스 토큰 없음 - 로그인 필요')
    showLoginRedirectModal('로그인이 필요합니다.')
    return { shouldRetry: false, handled: true }
  }

  try {
    // 2. 토큰 유효성 API로 확인
    await getCheckTokenApi()

    // 토큰이 유효한데 에러가 발생한 경우 - error.data.message 표시
    const errorMessage =
      error?.response?.data?.message || error?.data?.message || error?.message || '알 수 없는 오류가 발생했습니다.'
    showErrorToast(errorMessage)
    return { shouldRetry: false, handled: true }
  } catch {
    logger.debug('토큰 무효 - 재발급 시도')

    // 3. 토큰 유효성 확인에서 에러 발생 - 네이티브에 토큰 갱신 요청
    try {
      const newAccessToken = await requestTokenRefreshFromNative()

      logger.success('토큰 재발급 성공 - 요청 재시도 준비')

      return {
        shouldRetry: true,
        retryWithNewToken: newAccessToken,
        handled: true
      }
    } catch (refreshError) {
      logger.error('토큰 재발급 실패', refreshError)

      // 4. 네이티브 갱신 실패 - 로그인 화면으로 이동
      showLoginRedirectModal('로그인 세션이 만료되었습니다. 다시 로그인해주세요.')
      handleLogout()

      return { shouldRetry: false, handled: true }
    }
  }
}

// 요청 인터셉터: 모든 요청에 액세스 토큰 자동 포함 (메모리에서 조회)
axios.interceptors.request.use(
  config => {
    const accessToken = useAuthStore.getState().getAccessToken()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 응답 인터셉터: 토큰 만료 시 자동 갱신
axios.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    // 네트워크 오류 처리 (응답 없음)
    if (!error.response) {
      logger.error('네트워크 오류:', error.message)
      return Promise.reject(new Error('네트워크 연결을 확인해주세요.'))
    }

    const originalRequest = error.config

    // /auth/verify, /auth/check-token 요청은 인터셉터에서 제외 (무한 루프 방지)
    if (originalRequest.url?.includes('/auth/verify') || originalRequest.url?.includes('/auth/check-token')) {
      return Promise.reject(error)
    }

    // 토큰 관련 에러 조건 확인
    const isTokenError =
      error.response?.status === 401 ||
      (error.response?.status === 500 && error.response?.data?.message === '유효하지 않은 토큰입니다')

    // 토큰 에러이고, 아직 재시도하지 않은 경우
    if (isTokenError && !originalRequest._retry) {
      // 이미 토큰 갱신 중인 경우 대기열에 추가
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(axios(originalRequest))
            },
            reject
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // 네이티브에 토큰 갱신 요청
        const newAccessToken = await requestTokenRefreshFromNative()

        // React Query 캐시 무효화 (선택적)
        if (queryClient) {
          queryClient.invalidateQueries({
            queryKey: ['auth']
          })
        }

        // 대기 중인 요청들 처리
        processQueue(null, newAccessToken)

        // 원래 요청에 새 토큰 포함하여 재시도
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return axios(originalRequest)
      } catch (refreshError) {
        // 대기 중인 요청들에게 에러 전파
        processQueue(refreshError, null)

        // 로그아웃 처리
        handleLogout('토큰 갱신 실패')

        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default axios
